/*
  Lazy-load sections as they scroll into view.

  Two things happen per section, both driven by one IntersectionObserver:

    1. Render deferral - the section gets `.section-lazy` (content-visibility: auto)
       so the browser skips its layout and paint while it is off-screen. The class is
       removed permanently on first reveal, so the paint containment never applies to
       anything the shopper can see or interact with.

    2. Media hydration - `data-src` / `data-srcset` / deferred iframes and videos
       inside the section are promoted to real attributes, and anything lazysizes
       owns inside the section is unveiled immediately instead of waiting for the
       next throttled lazysizes pass.

  Sections above the fold are left alone so LCP is not delayed. Add `data-no-lazy`
  to a section wrapper to opt it out, or `data-disable-section-lazy` on <html> to
  kill the whole feature without redeploying assets.
*/
(function () {
  'use strict';

  if (document.documentElement.hasAttribute('data-disable-section-lazy')) return;

  var LAZY_CLASS = 'section-lazy';
  var EAGER_SECTIONS = 2; // above-the-fold sections that render normally
  var ROOT_MARGIN = '900px 0px';
  var SECTION_SELECTOR = '#MainContent > .shopify-section, .wrapper-footer > .shopify-section';
  var MEDIA_SELECTOR = [
    'img[data-src]',
    'img[data-srcset]',
    'source[data-src]',
    'source[data-srcset]',
    'iframe[data-src]',
    'video[data-src]'
  ].join(',');

  var supportsContentVisibility =
    window.CSS && CSS.supports && CSS.supports('content-visibility', 'auto');

  var observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(onIntersect, { rootMargin: ROOT_MARGIN, threshold: 0 })
      : null;

  function onIntersect(entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target);
      reveal(entry.target);
    });
  }

  function prepare() {
    var sections = document.querySelectorAll(SECTION_SELECTOR);

    Array.prototype.forEach.call(sections, function (section, index) {
      if (section.dataset.lazyState) return;

      if (index < EAGER_SECTIONS || section.hasAttribute('data-no-lazy')) {
        section.dataset.lazyState = 'eager';
        return;
      }

      section.dataset.lazyState = 'pending';

      if (!observer) {
        reveal(section);
        return;
      }

      if (supportsContentVisibility) section.classList.add(LAZY_CLASS);
      observer.observe(section);
    });
  }

  function reveal(section) {
    if (section.dataset.lazyState === 'revealed') return;
    section.dataset.lazyState = 'revealed';
    section.classList.remove(LAZY_CLASS);

    hydrate(section);
    relayout(section);

    section.dispatchEvent(new CustomEvent('section:inview', { bubbles: true }));
  }

  function hydrate(root) {
    Array.prototype.forEach.call(root.querySelectorAll(MEDIA_SELECTOR), function (el) {
      // Anything carrying lazysizes' own class is unveiled below instead, so its
      // .lazyloading / .lazyloaded state (which theme CSS styles) stays correct.
      if (el.classList.contains('lazyload')) return;
      // Product-card images belong to lazy-loading.js (per image, and card-slider
      // slides only on swipe / arrow / dot), so promoting them here would load
      // every slide of every card at once.
      if (el.classList.contains('lazy-image')) return;
      promote(el);
    });

    if (window.lazySizes && window.lazySizes.loader) {
      Array.prototype.forEach.call(root.querySelectorAll('.lazyload'), function (el) {
        try {
          window.lazySizes.loader.unveil(el);
        } catch (e) {
          /* lazysizes not ready yet - it will pick the element up on its own pass */
        }
      });
    }
  }

  function promote(el) {
    var sizes = el.getAttribute('data-sizes');
    var srcset = el.getAttribute('data-srcset');
    var src = el.getAttribute('data-src');

    if (sizes && sizes !== 'auto') {
      el.setAttribute('sizes', sizes);
      el.removeAttribute('data-sizes');
    }
    if (srcset) {
      el.setAttribute('srcset', srcset);
      el.removeAttribute('data-srcset');
    }
    if (src) {
      el.setAttribute('src', src);
      el.removeAttribute('data-src');
    }
    if (el.tagName === 'VIDEO' && typeof el.load === 'function') el.load();
  }

  // Slick measures slide widths at init. A section that was still skipped at init
  // time reports zero-width children, so nudge any slider inside it once the real
  // box exists. slider-component uses a ResizeObserver and recovers on its own.
  function relayout(section) {
    if (!window.jQuery || !window.jQuery.fn || !window.jQuery.fn.slick) return;

    requestAnimationFrame(function () {
      try {
        window.jQuery(section).find('.slick-initialized').slick('setPosition');
      } catch (e) {
        /* slider was torn down between reveal and this frame */
      }
    });
  }

  // A link straight to #some-id inside a skipped section: reveal it up front so the
  // browser lands on the right scroll position.
  function revealHashTarget() {
    if (!window.location.hash) return;

    var target;
    try {
      target = document.querySelector(window.location.hash);
    } catch (e) {
      return;
    }

    var section = target && target.closest('.shopify-section');
    if (section) reveal(section);
  }

  function init() {
    prepare();
    revealHashTarget();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('hashchange', revealHashTarget);

  // Theme editor: a re-rendered section is the one the merchant is looking at.
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.closest ? event.target.closest('.shopify-section') : null;
    prepare();
    if (section) reveal(section);
  });

  window.sectionLazy = { prepare: prepare, reveal: reveal };
})();
